import type { MaybeRefOrGetter } from "vue";
import { onUnmounted, toValue, watch } from "vue";

import { when } from "./wait";

const scrollPositions = new Map<string, number>();

/* Remember the last scroll position of an element. */
const useLastScrollPosition = (
  key: MaybeRefOrGetter<string>,
  element: MaybeRefOrGetter<HTMLElement | null | undefined>
) => {
  const saveScrollPosition = () => {
    const el = toValue(element);
    if (!el) {
      return;
    }

    scrollPositions.set(toValue(key), el.scrollTop);
  };

  let lastElement: HTMLElement | null | undefined;

  watch(
    () => toValue(key),
    async () => {
      await when(() => !!toValue(element));

      const el = toValue(element);
      if (!el) {
        return;
      }

      // Remove old event listener from previous element
      if (lastElement) {
        lastElement.removeEventListener("scroll", saveScrollPosition);
      }
      lastElement = el;

      const savedPosition = scrollPositions.get(toValue(key));
      el.scrollTop = savedPosition ?? 0;

      el.addEventListener("scroll", saveScrollPosition);
    },
    { immediate: true }
  );

  onUnmounted(() => {
    if (lastElement) {
      lastElement.removeEventListener("scroll", saveScrollPosition);
    }
  });
};

export default useLastScrollPosition;
